<template>
	<div>
        <div v-if="isModal" @click="hideModal" class="absolute top-5 right-5">
            <inline-svg name="close" classes="fill-current text-gray-300 hover:text-gray-400 h-6 w-6 ml-5 cursor-pointer"></inline-svg>
        </div>
		<div class="mt-10 mx-12 relative flex justify-around">
			<div class="mt-4 absolute w-full h-2 bg-blue-700 rounded-full"></div>
			<div @click="onStep = index" v-for="(step, index) in steps" :key="index" class="flex flex-col items-center z-30 cursor-pointer">
				<div class="wizard-step" :class="step.completed ? 'bg-green-500' : (index == onStep ? 'bg-orange-500' : 'bg-gray-400')">
					<inline-svg v-if="step.completed" name="checkmark" classes="w-4 h-4 text-white fill-current"></inline-svg>
					<inline-svg v-else :name="step.icon" classes="w-4 h-4 text-white fill-current"></inline-svg>
				</div>
				<div class="mt-2 font-medium" :class="index == onStep ? 'text-gray-800' : 'text-gray-600'">{{ step.label }}</div>
			</div>
		</div>
		<div class="mt-8 bg-gray-100 border rounded flex" :class="{ 'mx-24': ! isModal }">
			<div class="w-full">
				<div v-if="onStep == 'address'">
					<div class="mt-8 ml-8 flex items-center">
						<span class="wizard-step-number">01</span>
						<h2 class="ml-3 text-2xl text-gray-700">What's the address of the Building?</h2>
					</div>
			        <div v-if="buildings.all.length" class="mt-8">
			       		<span class="block alert red p-6">It looks like this Building may already exist in REoptimizer.</span>
			        </div>
                    <div class="mx-auto p-8 max-w-2xl flex flex-col">
                        <div class="relative rounded-lg">
                            <div class="p-8 flex flex-col">
                                <div class="flex mb-5">
                                    <div class="flex flex-col flex-grow">
                                        <label class="block mb-2" for="country">Country</label>
                                        <select v-model="building.country_id" @change="resetAddressValidation()" class="form-select">
                                            <option value=""></option>
                                            <option v-for="country in countries" :key="country.id" :value="country.id">{{ country.name }}</option>
                                        </select>
                                        <div v-if="errors.country_id" class="form-error">{{ errors.country_id[0] }}</div>
                                    </div>
                                </div>
                                <div class="mb-5">
                                    <label class="block mb-2" for="address_line_1">Address</label>
                                    <input v-model="building.address_line_1" @keyup="resetAddressValidation()" type="text" class="form-input w-full" placeholder="Required">
                                    <div v-if="errors.address_line_1" class="form-error">{{ errors.address_line_1[0] }}</div>
                                </div>
                                <div class="mb-5">
                                    <label class="block mb-2" for="address_line_2">Address 2</label>
                                    <input v-model="building.address_line_2" @keyup="resetAddressValidation()" type="text" class="form-input w-full">
                                    <div v-if="errors.address_line_2" class="form-error">{{ errors.address_line_2[0] }}</div>
                                </div>
                                <div class="flex justify-between mb-5">
                                    <div class="mr-4">
                                        <label class="block mb-2" for="city">City</label>
                                        <input v-model="building.city" @keyup="resetAddressValidation()" type="text" class="form-input" placeholder="Required">
                                        <div v-if="errors.city" class="form-error">{{ errors.city[0] }}</div>
                                    </div>
                                    <div v-if="building.country_id == 16" class="mr-4">
                                        <label class="block mb-2" for="state">State</label>
                                        <select v-model="building.state" @change="resetAddressValidation()" class="form-select max-w-fit">
                                            <option value=""></option>
                                            <option v-for="(state, stateIndex) in states" :key="stateIndex" :value="state.state">{{ state.state }}</option>
                                        </select>
                                        <div v-if="errors.state" class="form-error">{{ errors.state[0] }}</div>
                                    </div>
                                    <div>
                                        <label class="block mb-2" for="postal_code">Postal Code</label>
                                        <input v-model="building.postal_code" @keyup="resetAddressValidation()" type="text" class="form-input" placeholder="Required">
                                        <div v-if="errors.postal_code" class="form-error">{{ errors.postal_code[0] }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-center self-end">
                        	<div v-if="addressVerified" class="flex items-center">
                        		<inline-svg name="check" classes="w-6 h-6 text-green-500 fill-current"></inline-svg>
                        		<span class="ml-2 text-green-500 font-semibold">Address Verified</span>
                        	</div>
                        	<div class="self-end">
	                            <button v-if="! addressVerified" @click="verifyAddress" type="button" class="flex items-center btn btn-blue">
	                            	<p v-if="loading" class="py-2 flex justify-center">
	                            		<inline-svg name="loading-button"></inline-svg>
	                            	</p>
	                            	<span v-else>Verify Address</span>
	                            </button>
					            <button v-if="addressVerified" @click="completeStep('address', 'type')" type="button" class="ml-3 flex items-center py-2 pr-2 pl-4 btn btn-green">
					            	<span>Continue</span>
					            	<inline-svg name="chevron-right"></inline-svg>
					            </button>
							</div>
						</div>
                    </div>
			    </div>

				<div v-if="onStep == 'type'">
					<div class="mt-8 ml-8 flex items-center">
						<span class="wizard-step-number">02</span>
						<h2 class="ml-3 text-2xl text-gray-700">What kind of Building is this?</h2>
					</div>
					<div class="flex flex-col">
						<div class="mt-6 flex flex-wrap justify-center gap-4">
							<div
								v-for="type in buildingTypes" :key="type.id"
								class="flex flex-col relative bg-gray-50 hover:bg-gray-100 border-gray-300 shadow-md cursor-pointer"
								@click="building.type_id = type.id">
									<div v-if="type.id == building.type_id" class="absolute top-0 right-0 rounded-full -mt-3 -mr-3">
										<inline-svg name="check-large"></inline-svg>
									</div>
									<div :class="isModal ? 'p-8' : 'p-16'">
										<inline-svg :name="type.svg_name"></inline-svg>
									</div>
									<span
										class="p-4 text-center text-white uppercase"
										:class="type.id == building.type_id ? 'bg-green-600' : 'bg-gray-700'">
											{{ type.type }}
									</span>
							</div>
						</div>
	                    <div class="my-5 mr-5 self-end">
				            <button v-if="building.type_id != ''" @click="completeStep('type', 'details')" type="button" class="ml-3 flex items-center py-2 pr-2 pl-4 btn btn-green">
				            	<span>Continue</span>
				            	<inline-svg name="chevron-right"></inline-svg>
				            </button>
						</div>
					</div>
				</div>

			    <div v-if="onStep == 'details'" class="flex flex-col">
					<div class="mt-8 ml-8 flex items-center">
						<span class="wizard-step-number">03</span>
						<h2 class="ml-3 text-2xl text-gray-700">Building Details</h2>
					</div>
					<div v-if="buildingFields.length">
			        	<table class="my-8 w-full">
			        		<tbody>
			        			<tr v-for="i in Math.ceil(buildingFields.length / 4)" :key="i" class="flex">
									<td v-for="(row, rowIndex) in buildingFields.slice((i - 1) * 4, i * 4)" :key="rowIndex" class="flex items-center justify-between w-1/4 text-xl">
										<div class="text-sm font-medium w-1/2 ml-8 leading-normal">{{ row.label }}</div>
										<div class="ml-4">
											<div v-if="row.type == 'text'">
												<input :name="row.field" v-model="building[row.field]" class="form-input" size="10">
											</div>
											<div v-if="row.type == 'integer' || row.type == 'double'" class="text-right">
												<number-input :name="row.field" v-model="building[row.field]"></number-input>
											</div>
											<div v-else-if="row.type == 'boolean'">
												<toggle-button v-model="building[row.field]" color="#38C172" sync></toggle-button>
											</div>
											<div v-else-if="row.type == 'value-list'">
												<select v-model="building[row.field]" class="form-select">
													<option value=""></option>
													<option v-for="(option, optionIndex) in row.options" :key="optionIndex" :value="option.option">{{ option.option }}</option>
												</select>
											</div>
										</div>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div v-else class="mt-10">
						<p class="block alert red p-6">You must <button @click="onStep = 'type'" type="button" class="text-red-800 font-bold underline">select a Building type</button></p>
					</div>
                    <div class="my-5 mr-5 self-end">
			            <button v-if="building.type_id" @click="completeStep('details', 'unit')" type="button" class="ml-3 flex items-center py-2 pr-2 pl-4 btn btn-green">
			            	<span>Continue</span>
			            	<inline-svg name="chevron-right"></inline-svg>
			            </button>
					</div>
			    </div>

                <div v-if="onStep == 'unit'" class="flex flex-col">
                    <div class="mt-8 ml-8 flex items-center">
                        <span class="wizard-step-number">04</span>
                        <h2 class="ml-3 text-2xl text-gray-700">Add your unit</h2>
                    </div>
                    <form class="my-8 p-6 border bg-gray-50">
                        <div class="px-2 py-3 flex flex-wrap gap-y-6 gap-x-8">
                            <div class="flex flex-col">
                                <label class="text-sm font-semibold" for="floor">Floor</label>
                                <number-input name="floor" v-model="unit.floor" classes="text-sm text-right"></number-input>
                                <div v-if="errors.floor" class="form-error">{{ errors.floor[0] }}</div>
                            </div>

                            <div class="flex flex-col">
                                <label class="text-sm font-semibold" for="suite">Suite</label>
                                <input type="text" class="form-input" name="suite" size="6" v-model="unit.suite">
                                <div v-if="errors.suite" class="form-error">{{ errors.suite[0] }}</div>
                            </div>

                            <div class="flex flex-col">
                                <label class="text-sm font-semibold" for="description">Status</label>
                                <select class="mt-0 form-select flex-none max-w-fit" name="status" v-model="unit.status">
                                    <option value=""></option>
                                    <option value="Available">Available</option>
                                    <option value="Occupied">Occupied</option>
                                    <option value="Sublease">Sublease</option>
                                    <option value="Vacant">Vacant</option>
                                </select>
                                <div v-if="errors.status" class="form-error">{{ errors.status[0] }}</div>
                            </div>

                            <div class="flex flex-col">
                                <label class="text-sm font-semibold" for="size">Size</label>
                                <number-input name="size" v-model="unit.size" classes="text-sm text-right"></number-input>
                                <div v-if="errors.size" class="form-error">{{ errors.size[0] }}</div>
                            </div>

                            <div class="flex flex-col">
                                <label class="text-sm font-semibold" for="divisble">Divisible</label>
                                <number-input name="divisible" v-model="unit.divisible" classes="text-sm text-right"></number-input>
                                <div v-if="errors.divisible" class="form-error">{{ errors.divisible[0] }}</div>
                            </div>

                            <div class="flex flex-col">
                                <label class="text-sm font-semibold">Renewal Space?</label>
                                <select class="mt-0 form-select flex-none max-w-fit" name="status" v-model="unit.is_renewal_space">
                                    <option value=""></option>
                                    <option value="0">No</option>
                                    <option value="1">Yes</option>
                                </select>
                                <div v-if="errors.is_renewal_space" class="form-error">{{ errors.is_renewal_space[0] }}</div>
                            </div>

                            <div class="flex flex-col">
                                <label class="text-sm font-semibold" for="ti_allowance">TI Allowance</label>
                                <currency-input name="ti_allowance" v-model="unit.ti_allowance" is-decimal></currency-input>
                                <div v-if="errors.ti_allowance" class="form-error">{{ errors.ti_allowance[0] }}</div>
                            </div>

                            <div class="w-full flex flex-col">
                                <label class="text-sm font-semibold" for="description">Comments</label>
                                <textarea class="w-full form-textarea" name="comment" rows="6" v-model="unit.comment" :v-text="unit.comment"></textarea>
                                <div v-if="errors.comment" class="form-error">{{ errors.comment[0] }}</div>
                            </div>
                        </div>
                    </form>
                    <div class="mb-5 mr-5 self-end">
                        <button v-if="verifyUnit" @click="completeStep('unit', 'finalize')" type="button" class="ml-3 flex items-center py-2 pr-2 pl-4 btn btn-green">
                            <span>Continue</span>
                            <inline-svg name="chevron-right"></inline-svg>
                        </button>
                    </div>
                </div>

			    <div v-if="onStep == 'finalize'">
					<div class="mt-8 ml-8 flex items-center">
						<span class="wizard-step-number">05</span>
						<h2 class="ml-3 text-2xl text-gray-700">Finalize Building</h2>
					</div>
					<div v-if="! readyForCreation()" class="mt-10">
						<p class="block alert red p-6">You must complete all previous steps before creating your Building</p>
					</div>
                    <div class="mt-4 flex flex-col items-center justify-center">
						<div v-if="readyForCreation()" class="text-lg mb-4">Almost there, confirm the information below then click <strong>Create Building</strong>.</div>
	                    <div class="flex flex-col w-2/5 shadow-lg rounded-lg">
	                        <div class="relative overflow-hidden">
	                            <img class="w-full object-cover rounded-t-lg" :class="isModal ? 'h-32' : 'h-64'" src="/img/site.jpg">
	                        </div>
	                        <div class="flex items-center justify-between">
		                        <div v-if="building.address_line_1" class="flex flex-col">
			                        <div class="p-4 leading-normal">
			                            <p class="text-lg font-medium">{{ building.address_line_1 }}</p>
			                            <p class="text-sm">{{ building.city }}, {{ building.state }}  {{ building.postal_code }}</p>
			                        </div>
								</div>
								<div class="mr-6 text-xl font-bold">{{ buildingTypeName }}</div>
							</div>
	                    </div>
			        	<table class="my-8 w-full">
			        		<tbody>
			        			<tr v-for="i in Math.ceil(buildingFields.length / 4)" :key="i" class="flex">
									<td v-for="(row, rowIndex) in buildingFields.slice((i - 1) * 4, i * 4)" :key="rowIndex" class="flex items-center justify-between w-1/4 text-xl">
										<div class="text-sm font-medium w-1/2 ml-8 leading-normal">{{ row.label }}</div>
										<div v-if="row.type == 'boolean'">
											<inline-svg v-if="building[row.field]" name="check"></inline-svg>
											<inline-svg v-else name="x"></inline-svg>
										</div>
										<div v-else>{{ building[row.field] }}</div>
									</td>
								</tr>
							</tbody>
						</table>
						<button v-if="readyForCreation()" @click="createBuilding" type="button" class="mb-6 py-4 px-12 btn btn-green text-3xl font-extrabold">Create Building</button>
					</div>
			    </div>
			</div>
		</div>

		<notifications classes="notification" position="bottom right"></notifications>
	</div>
</template>

<script>
	import { mapState } from 'vuex';

    export default {
    	props: {
    		activeStep: {
    			type: String,
    			default: 'address'
    		},

    		isModal: {
    			type: Boolean,
    			default: false
    		},

    		forSite: {
    			type: Boolean,
    			default: false
    		}
    	},

		data: function () {
		    return {
				steps: {
	            	'address': {
		                'icon': 'location',
		                'label': 'Address',
		                'completed': false
		            },

		            'type': {
		                'icon': 'type',
		                'label': 'Type',
		                'completed': false
		            },

                    'details': {
                        'icon': 'details',
                        'label': 'Details',
                        'completed': false
                    },

                    'unit': {
                        'icon': 'type',
                        'label': 'Unit',
                        'completed': false
                    },

		            'finalize': {
		                'icon': 'save',
		                'label': 'Finalize',
		                'completed': false
		            }
				},
		    	onStep: this.activeStep,
		    	loading: false,

		    	errors: [],
                countries: {},
                states: {},
		    	building: {
		    		'type_id': null,
		    		'country_id': 16
		    	},
                unit: {
                    floor: null,
                    status: null,
                    size: 0,
                    divisible: 0,
                    is_renewal_space: 0,
                    ti_allowance: 0
                },
		    	addressVerified: false
		    }
		},

        mounted() {
            this.fetchAddressInfo();
            this.fetchBuildingTypes();
        },

		computed: {
			...mapState(['buildings']),

			buildingFields() {
				if (this.building.type_id != null) {
					return _.find(this.buildingTypes, ['id', this.building.type_id]).fields;
				}

				return [];
			},

        	buildingTypeName() {
        		let buildingType = _.find(this.buildingTypes, ['id', this.building.type_id]);

        		if (buildingType) {
        			return buildingType.type;
        		}

        		return null;
        	},

            verifyUnit() {
                if (this.unit.floor != null && this.unit.status != null && this.unit.size != 0) {
                    return true;
                }

                return false;
            }
		},

        methods: {
			resetAddressValidation() {
				this.addressVerified = false;
			},
        	fetchAddressInfo() {
	            axios.get('/api/address-info').then((response) => {
	                this.countries = response.data.countries;
	                this.states = response.data.states;
	            }).catch((error) => {
	                console.log(error);
	            });
        	},

        	fetchBuildingTypes() {
	            axios.get('/api/building-types').then((response) => {
	                this.buildingTypes = response.data.types;
	            }).catch((error) => {
	                console.log(error);
	            });
        	},

        	completeStep(stepToComplete, nextStep) {
	        	let step = this.findStep(stepToComplete);

	        	step.completed = true;

	        	this.onStep = nextStep;
        	},

        	findStep(stepName) {
        		return _.find(this.steps, (step, index) => {
					return index === stepName;
	        	});
        	},

            /**
             * Helper method to get address component from Google's response
             */
            getAddressComponent(components, type, shortName = false) {
                if (!components[type]) return null;
                const component = components[type];
                return shortName ? component.short_name : component.long_name;
            },

            verifyAddress() {
                this.loading = true;
                this.addressVerified = false;
                this.errors = [];

                axios.post('/api/validate-building', this.building)
                    .then(response => {
                        if (response.data.success) {
                            const data = response.data.data;
                            const components = data.address_components || {};
                            const location = data.location || {};
                            
                            // Map Google's address components to our format
                            const addressData = {
                                ...this.building, // Keep existing data
                                address_line_1: (() => {
                                    const streetNumber = this.getAddressComponent(components, 'street_number');
                                    const route = this.getAddressComponent(components, 'route');
                                    const addressLine1 = [streetNumber, route].filter(Boolean).join(' ');
                                    return addressLine1 || this.building.address_line_1; // Fallback to original input if no street number/route found
                                })(),
                                city: this.getAddressComponent(components, 'locality') || 
                                      this.getAddressComponent(components, 'administrative_area_level_2'),
                                state: this.getAddressComponent(components, 'administrative_area_level_1', true),
                                postal_code: this.getAddressComponent(components, 'postal_code'),
                                latitude: location.lat,
                                longitude: location.lng,
                                formatted_address: data.formatted_address,
                                place_id: data.place_id
                            };
                            
                            // Update building data
                            this.building = addressData;
                            this.addressVerified = true;
                            
                            this.$notify({
                                type: 'success',
                                title: 'Success',
                                text: 'Address verified successfully!',
                                duration: 3000
                            });
                        } else {
                            this.$notify({
                                type: 'error',
                                title: 'Error',
                                text: response.data?.message || 'Failed to verify address',
                                duration: 5000
                            });
                        }
                    })
                    .catch(error => {
                        const message = error.response?.data?.message || 'Error validating address';
                        this.errors = error.response?.data?.errors || {};
                        
                        this.$notify({
                            type: 'error',
                            title: 'Validation Error',
                            text: message,
                            duration: 5000
                        });
                    })
                    .finally(() => {
                        this.loading = false;
                    });
            },

            readyForCreation() {
            	// find all steps except 'Finalize'
            	let steps = _.filter(this.steps, (step, index) => {
                	return index !== 'finalize';
                });

                return _.every(steps, 'completed');
            },

            createBuilding() {
				axios.post('/buildings', this.building).then(async (response) => {
                    // create unit
                    this.unit.building_id = response.data.building.id;
                    await this.createUnit();

                    this.$notify({
                        type: 'success',
                        title: 'Success',
                        text: response.data.message
                    });

                    setTimeout(() => {
                    	if (this.forSite) {
                    		this.$emit('buildingCreated', response.data.building);
                    		this.$modal.hide('create-building');
                    	} else {
							window.location = '/buildings/' + response.data.building.id + '/edit';
                    	}
                    }, 1000);
				}).catch(error => {
					console.log(error);
				});
            },

            async createUnit() {
                await axios.post('/units', this.unit)
                    .catch((error) => {
                        console.log(error);
                        this.errors = error.response.data.errors;
                    });
            }
        }
    }
</script>
